import { FIXTURE_ID_PATTERN } from "./app-admin-inputs.js";
import { pagedEnvelopeSchema, pagedToolFields } from "./paging.js";
import { z } from "zod";
//#region src/app-restart-history.ts
/**
* Container-restart history contract — apps-MCP v2 spine V-6 (seam S5),
* `GET /v1/admin/apps/:appId/restart-history`. One slice of #825.
*
* *"Is it still flapping?"* — the only durable record of container restarts the
* platform has. `restartCount` on a live pod resets when the pod is replaced (a
* rollout, an eviction, a node drain), and Kubernetes events expire in about an
* hour, so neither can answer the question across pod replacements or across days.
*
* Rows come from `release.app_restart_events`, appended by release-engine from the
* DP operator's `StatusReport.podLifecycle`.
*
* ⛔ **The paging shape is NOT declared here.** The request fragment, the response
* envelope and the cursor codec all come from `./paging.js` (spine V-1; ruled R-7 /
* R-17 E2(a)) — one contract for every collection read on this surface. Do not mint
* a second one.
*/
/** Max page size for this endpoint. The DEFAULT is the house `PAGE_DEFAULT_LIMIT`
*  (100) — ⛔ not re-declared here; V-1's "default <= 100" is a surface-wide rule and
*  a second copy of the number is a second thing to keep in step. */
const RESTART_HISTORY_MAX_LIMIT = 500;
/**
* One recorded container restart.
*
* ⚠️ `exitCode` and `reason` describe the run that **ENDED**, and
* `restartCountAfter` is the restart count observed alongside it. So a row is
* evidence of a death, not a statement about the currently-running container.
*
* ⚠️ **`reason` is `z.string()`, deliberately.** The kubelet's termination-reason
* vocabulary is open-world (`OOMKilled`, `Error`, `Completed`,
* `ContainerStatusUnknown`, `DeadlineExceeded`, plus runtime-specific values), and
* `release.app_restart_events.reason` carries no CHECK. This is an append-only
* ledger of values already written to disk, possibly by an older producer — the same
* reasoning that keeps `AppHistoryState.health` a bare `string`. `'Unknown'` appears
* when the kubelet reported no reason at all: a label for "not stated", never an
* inference.
*
* ⚠️ **`restartCountAfter` gaps are INFORMATION, not corruption.** The DP samples
* about every 30 seconds and Kubernetes retains only the most recent termination per
* container, so a burst of restarts between two samples yields ONE row. A jump from
* `3` to `9` means six restarts happened and five were never recorded. Read the
* sequence, not just the row count.
*
* ## ⛔ PROVENANCE — every field here originated on a DATA PLANE (R-31.2b)
*
* `dpId` is authenticated (mTLS CN == the URL `:dpId` at ingest). Everything else — the
* app identity, the fixture, the pod, the container, the exit code, the reason and the
* instant — was read off a pod by the reporting operator and forwarded verbatim. CP
* fences the (dp, app) pair at ingest against `release.dp_fixture_status` so a DP cannot
* append history for an app it does not host, and the shapes are schema-constrained on
* the wire; nothing else is corroborated.
*
* ⇒ ⛔ **Treat `reason` as UNTRUSTED DISPLAY TEXT.** It is bounded (256) but open-vocabulary
* by design, and it is the one field an agent is told to reason from — so do not branch on
* it, do not parse it, and do not build a CP-side taxonomy over it.
*/
const appRestartEventSchema = z.object({
	/** Which data plane the pod was running on. */
	dpId: z.string(),
	/** The fixture the pod belonged to. */
	fixtureId: z.string(),
	podName: z.string(),
	/** The pod's Kubernetes uid — stable across a name reuse, and gone with the pod. */
	podUid: z.string(),
	/** Container name. Unique within a pod across containers AND init containers. */
	containerName: z.string(),
	/** Exit status of the run that ENDED. `137` = SIGKILL, `143` = SIGTERM. */
	exitCode: z.number().int(),
	/** Kubelet's brief reason for that termination — see the caveat above. */
	reason: z.string(),
	/** Restart count observed alongside the termination. `>= 1`. */
	restartCountAfter: z.number().int().positive(),
	/** ISO-8601 instant the run finished — the KUBELET's clock, not CP's.
	*  ⛔ `.max(64)` mirrors the WIRE bound on `PodLifecycleRecordSchema.at`
	*  (`@zion/sdk-cp-dp-contracts`), whose absence falsified the operator's
	*  carry-always-drains liveness proof. Read and write domains stay equal. */
	occurredAt: z.string().max(64)
}).strict();
/**
* One page of restart history — the HOUSE V-1 envelope, not a local shape.
*
* `items` is the default key (R-7: the key is parameterized only so EXISTING domain
* keys such as `pods` can keep their names; a new envelope uses `items`).
*
* ⚠️ **`truncated` means "more existed than you received, for ANY cause"** (R-4 as
* clarified by **R-25**) — a page remainder OR an uncontinuable server cap. One field
* with one meaning on every collection read, so an agent never has to know which kind
* of tool it is holding; `nextCursor` carries the remedy, and
* `truncated && nextCursor === null` is precisely "a cap you cannot continue past".
*
* ⛔ **The universal law is the ONE-directional implication**
* `nextCursor !== null ⇒ truncated === true`. R-4's original formula
* `truncated ≡ (nextCursor !== null)` is NOT the rule: on a hard-ceilinged route it
* forces `truncated: false` on the last page while rows sit on no page at all — the
* exact lie this program exists to kill.
*
* ⭐ On THIS endpoint the full equivalence does hold, and for a stated reason rather
* than by assumption: there is **no cap concept** here. Nothing slices the result set
* before paging (contrast `AppPodsService`'s `MAX_PODS`), the ledger is bounded only
* by retention, and the keyset walks to the end. So `truncated` can only ever mean
* "another page exists". §6.8 arm 7 asserts BOTH directions and says why it may.
*
* ⚠️ **An empty `items` means "no restart was RECORDED", never "the app is
* healthy".** History begins when this feature was deployed — there is no backfill —
* and rows are pruned 30 days after ingest. C-4: the surface says what it cannot know.
*/
const appRestartHistoryPageSchema = pagedEnvelopeSchema(appRestartEventSchema);
/**
* Query/argument schema for the route AND the tool.
*
* ⚠️ **`pagedToolFields`, not `pagedQueryFields`** — so `limit` carries a
* `.default()`. ⛔ **This is EXPLICITLY sanctioned, not an oversight: R-28.6.**
* R-26's universal rule ("a `.default()` on a limit belongs only in tool-args schemas,
* never in a schema a legacy route caller reaches") protects routes with EXISTING
* callers — `appPodsQuerySchema` is the case that earned it, where a default would have
* silently dropped a live operator surface from 500 pods to 100. **A brand-new route
* born paged has no legacy caller to truncate**, so it may default; R-28.6 instructs
* reviewers not to flag this. Also R-6: in-process site-admin-api routes are
* additive-only and self-selection is an upstream-REST-only device. `appHistoryQuerySchema`
* is the in-tree precedent — it defaults too.
*
* Bound at the controller via `@Query({ schema })`; the global
* `ZionStrictValidationPipe` rejects malformed input with a 400 before the service.
* Load-bearing, not decorative: `?limit=foo` would otherwise reach the SQL as
* `LIMIT NaN`, and a bad `cursor` would reach the `::timestamptz` / `::bigint` casts
* and surface as a 500 with a DB-error leak.
*
* `.strict()` (#466) is what keeps `orgId`, `dpId`-as-authz, `offset` and any
* free-text sort key off this surface by construction. The app scope comes from the
* PATH param plus the principal's grants — never from the query string.
*
* ⚠️ **`since` has no MAX bound, unlike E11's 90 days, and that is deliberate.** The
* table retains 30 days, so an older `since` cannot ask for more than exists;
* refusing it would be refusing a legitimate "give me everything". The retention
* bound is stated in the route and tool descriptions, where a reader will see it.
*
* ⛔ These two filters and nothing else: the tool's argument set and the service's
* parameter set are held equal by a D-7 witness pair, so adding one here without the
* other end is a compile error rather than a lying surface.
*/
const appRestartHistoryQuerySchema = z.object({
	/**
	* Narrow to one data plane (R-35.5 — ADDED on both ends by ruling).
	*
	* ⭐ The tool's whole job — *"is it still flapping"* — is per-DP on a multi-DP app,
	* the wire item already carries `dpId` (so an agent can SEE the dimension), and the
	* exemplar `app-history` has this exact filter (`app-history.controller.ts:68`).
	* A D-7 witness pair is an argument for putting it on BOTH ends, never for omitting it.
	*
	* ⛔ It is a FILTER, never an authorization input: a DP the app does not run on
	* yields an empty page, and app scope comes from the PATH param plus the principal's
	* grants. It is deliberately not a path segment — a DP-typed value on the app-typed
	* `@RequireResourcePermission` param would 403 universally while reading as correct.
	*/
	dpId: z.uuid().optional(),
	/** Restrict to one fixture id. DNS-1123 label — the producer's own domain, so a
	*  value outside it can match no stored row, and the WIRE schema enforces the same
	*  pattern (§2.2) so the write domain equals this one. */
	fixtureId: z.string().regex(FIXTURE_ID_PATTERN).optional(),
	/** ISO-8601 lower bound on `occurredAt`. Absent ⇒ everything retained. */
	since: z.string().datetime({ offset: true }).optional(),
	...pagedToolFields({ maxLimit: 500 })
}).strict();
/** Decimal-digit grammar for the cursor's `i`. Declared ONCE so the `.regex()` check and
*  the totality guard inside the value refinement cannot drift apart. No `g` flag, so
*  `.test()` is stateless and safe to call repeatedly. */
const DECIMAL_DIGITS = /^\d+$/;
/** PostgreSQL `bigint` (int8) upper bound. `release.app_restart_events.id` is a
*  `bigserial` and the read path renders the cursor's `i` into a `::bigint` cast, so this
*  — not a digit count — is the field's real domain. */
const INT8_MAX = 9223372036854775807n;
/** The keyset cursor payload for this endpoint: `(occurred_at, id)`.
*  ⛔ `.strict()` — a tampered token carrying extra keys must land on
*  `InvalidPageCursorError` like every other malformation (`decodeKeysetCursor`
*  requires a strict schema). `t` is an ISO instant at MICROSECOND precision (the
*  read path renders it in SQL); `i` is the bigint id as its decimal string. */
const restartCursorPayloadSchema = z.object({
	t: z.string().datetime({ offset: true }),
	/**
	* The bigint id as its decimal string. It reaches a `::bigint` cast on the
	* read path, so its DOMAIN is `int8`, not "digits".
	*
	* ⛔ **`.max(20)` is R-88 SEC-3.** Without it this was the ONE unbounded
	* cursor field on the surface while every sibling is bounded (uuid / 64 /
	* 128 / 255). Measured: `{t, i: '9'×330}` base64url-encodes to **507
	* characters** — under `PAGE_CURSOR_MAX` (512) and clean on the charset
	* regex — so an arbitrarily long digit string reached `SELECT $1::bigint`,
	* which raises **SQLSTATE 22003** and surfaces as the 500 this cursor's own
	* docblock promises to prevent.
	*
	* ✅ **THE RESIDUAL IS NOW CLOSED — `.refine` is the control (R-95).** dev-ctr3
	* measured and stated it rather than papering over it, and correctly declined to
	* close it without a ruling: `.max(20)` DOES NOT stop the 22003, because `int8`'s
	* maximum `9223372036854775807` is **19 digits**, so the largest LEGAL value and the
	* smallest OVERFLOWING value (`9223372036854775808`, R-88's own probe) are the same
	* length. **A LENGTH BOUND CANNOT EXPRESS A VALUE BOUND** — the same category error
	* as R-71's *a count bound cannot express a byte bound*. The value check is now
	* ruled and applied.
	*
	* `.max(20)` KEEPS its place as defence-in-depth: it caps the ReDoS surface and kills
	* the unbounded string (330 digits ⇒ a 507-char token, under `PAGE_CURSOR_MAX` 512
	* and charset-clean, so the outer bound admitted it).
	* ⚠️ Reordered to `.max()` BEFORE `.regex()` — the repo's ReDoS rule is that length is
	* bounded before the match runs.
	*
	* ⛔⛔ **`DECIMAL_DIGITS.test(v)` INSIDE THE REFINEMENT IS NOT REDUNDANT — IT IS WHAT
	* MAKES THE PREDICATE TOTAL.** Measured on zod 4: a `.refine()` runs **even when the
	* preceding string checks have already failed**, so a bare `BigInt(v) <= INT8_MAX`
	* throws a raw `SyntaxError: Cannot convert abc to a BigInt` straight out of
	* `safeParse` on `i: 'abc'` — converting a clean 400 into an uncaught 500, a worse
	* bug than the one being fixed, on an input arm 11 already tests. Do not
	* "simplify" it away; arm 11's `'abc'` case is the standing regression guard.
	*/
	i: z.string().max(20).regex(DECIMAL_DIGITS).refine((v) => DECIMAL_DIGITS.test(v) && BigInt(v) <= INT8_MAX, { message: "cursor id is out of range for a 64-bit integer" })
}).strict();
//#endregion
export { RESTART_HISTORY_MAX_LIMIT, appRestartEventSchema, appRestartHistoryPageSchema, appRestartHistoryQuerySchema, restartCursorPayloadSchema };

//# sourceMappingURL=app-restart-history.js.map